import React, { FC, useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  Polyline,
  LayersControl,
  LayerGroup,
  CircleMarker,
  Tooltip as MapTooltip,
  useMap,
} from "react-leaflet";
import { useLeafletContext } from "@react-leaflet/core";
import L, { LatLngExpression, LatLngTuple } from "leaflet";
import "leaflet.heat";
import { Box, Chip, Stack, Typography } from "@mui/material";
import "leaflet/dist/leaflet.css";
import { useGeofenceSearch } from "@/hooks/useGeofences";
import {
  GeoFenceAction,
  GeofenceListItem,
} from "@/domain/geofence.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";

// Build a coloured pin via SVG. Avoids shipping the default Leaflet PNGs and
// the bundler-aware require() dance, and lets the legend chips share the same
// colour palette without drift.
const makePin = (hex: string): L.DivIcon =>
  L.divIcon({
    className: "geofence-map-pin",
    html: `<svg width="28" height="38" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0 C6.27 0 0 6.05 0 13.5 C0 23.5 14 38 14 38 C14 38 28 23.5 28 13.5 C28 6.05 21.73 0 14 0 Z"
        fill="${hex}" stroke="white" stroke-width="2"/>
      <circle cx="14" cy="13.5" r="5" fill="white"/>
    </svg>`,
    iconSize: [28, 38],
    iconAnchor: [14, 38],
    popupAnchor: [0, -34],
  });

const PRIMARY_COLOR = "#1976d2";
const SECONDARY_COLOR = "#2e7d32";
const EVENT_COLOR = "#d32f2f";
const TRAJECTORY_LINE_COLOR = "#616161";
const TRAJECTORY_REAL_COLOR = "#2e7d32";
const TRAJECTORY_PHANTOM_COLOR = "#d32f2f";

const primaryIcon = makePin(PRIMARY_COLOR);
const secondaryIcon = makePin(SECONDARY_COLOR);
const eventIcon = makePin(EVENT_COLOR);

// COYN-1347: trajectory page size. We surface only the latest few events so
// the polyline doesn't degenerate into noise on long-running enrollments.
const TRAJECTORY_PAGE_SIZE = 15;
// COYN-1347: heatmap page size. Larger window so the heat overlay reflects
// the participant's full behaviour rather than just the last hour.
const HEATMAP_PAGE_SIZE = 100;

export interface GeofenceMapPoint {
  lat: number;
  lng: number;
  label: string;
}

export interface GeofenceMapProps {
  primary?: GeofenceMapPoint;
  secondary?: GeofenceMapPoint;
  event: GeofenceMapPoint;
  // Centre that the radius circle is drawn around (matches the event's
  // addressType). When undefined we skip the circle and just show markers.
  activeCenter?: GeofenceMapPoint;
  radiusMeters?: number;
  // COYN-1347: accuracy threshold from program configuration. When provided,
  // the map draws a second concentric circle marking the phantom-EXIT gate's
  // inner threshold — the zone where the backend would have suppressed the
  // VALIDATION_REQUEST. Undefined → no inner circle.
  accuracyThresholdMeters?: number;
  // COYN-1347: participant whose recent events power the trajectory and
  // heatmap overlays. When undefined those overlays stay hidden.
  participantId?: string;
  // COYN-1347: id of the event we're looking at, so its dot in the trajectory
  // overlay gets a highlight ring to distinguish it from siblings.
  currentEventId?: string;
  // i18n strings injected from the caller so the component stays free of
  // a translation dependency.
  labels: {
    primary: string;
    secondary: string;
    event: string;
    radius: string;
    openInGoogleMaps: string;
    bufferCircle?: string;
    trajectory?: string;
    trajectoryHint?: string;
    heatmap?: string;
    tooltipPhantom?: string;
    tooltipInferredPhantom?: string;
    tooltipReal?: string;
    tooltipInferredReal?: string;
    dateFormat?: string;
  };
  height?: number | string;
}

// Loose-cast helper: react-leaflet v5 declares props as `unknown` in some
// types when paired with React 19; the JSX itself is fully typed at runtime
// and these wrappers exist purely to avoid one-off `any`s sprinkled in the
// tree below.
const Map = MapContainer as unknown as React.ComponentType<
  React.PropsWithChildren<{
    center: LatLngExpression;
    zoom: number;
    style: React.CSSProperties;
    scrollWheelZoom: boolean;
  }>
>;

const Tiles = TileLayer as unknown as React.ComponentType<{
  url: string;
  attribution: string;
}>;

const Pin = Marker as unknown as React.ComponentType<
  React.PropsWithChildren<{ position: LatLngExpression; icon: L.DivIcon }>
>;

const RadiusCircle = Circle as unknown as React.ComponentType<{
  center: LatLngExpression;
  radius: number;
  pathOptions: L.PathOptions;
}>;

const Line = Polyline as unknown as React.ComponentType<{
  positions: LatLngExpression[];
  pathOptions: L.PathOptions;
}>;

const Dot = CircleMarker as unknown as React.ComponentType<
  React.PropsWithChildren<{
    center: LatLngExpression;
    radius: number;
    pathOptions: L.PathOptions;
  }>
>;

const OverlayLayer = LayersControl.Overlay as unknown as React.ComponentType<
  React.PropsWithChildren<{
    name: string;
    checked?: boolean;
  }>
>;

const Group = LayerGroup as unknown as React.ComponentType<
  React.PropsWithChildren<Record<string, unknown>>
>;

const HoverTip = MapTooltip as unknown as React.ComponentType<
  React.PropsWithChildren<Record<string, unknown>>
>;

const computeBounds = (points: GeofenceMapPoint[]): LatLngTuple => {
  // Centre on the mean of the available points so the initial view covers
  // every marker without depending on Leaflet's fitBounds (which fights with
  // SSR-safe rendering).
  const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const lng = points.reduce((s, p) => s + p.lng, 0) / points.length;
  return [lat, lng];
};

const googleMapsUrl = (points: GeofenceMapPoint[]): string => {
  // Drop the user in Google Maps directions with the event as origin and
  // the primary (or secondary, whichever is available) as destination so
  // they can verify the offset against an authoritative source.
  const [first, ...rest] = points;
  if (rest.length === 0) {
    return `https://www.google.com/maps?q=${first.lat},${first.lng}`;
  }
  const dest = rest[rest.length - 1];
  const waypoints = rest
    .slice(0, -1)
    .map((p) => `${p.lat},${p.lng}`)
    .join("|");
  const base = `https://www.google.com/maps/dir/?api=1&origin=${first.lat},${first.lng}&destination=${dest.lat},${dest.lng}`;
  return waypoints ? `${base}&waypoints=${encodeURIComponent(waypoints)}` : base;
};

// COYN-1347: derive the gate's inner threshold radius. Mirrors the backend
// formula (geofence.service.ts) so the visualisation accurately represents
// the rejected zone:
//   innerRadius = radius - max(accuracyThreshold × 10, 50m)
// Clamped to 0 to keep Leaflet from drawing a negative-radius circle.
const computeBufferRadius = (
  geofenceRadius: number,
  accuracyThreshold: number,
): number => {
  const headroom = Math.max(accuracyThreshold * 10, 50);
  return Math.max(geofenceRadius - headroom, 0);
};

// COYN-1347: derive the verdict (phantom/real, inferred or gated) for a row,
// so each trajectory dot is coloured and labelled consistently with the chip
// shown in the list and detail pages.
type TrajectoryVerdict = "phantom" | "inferredPhantom" | "real" | "inferredReal";

const verdictFor = (row: GeofenceListItem): TrajectoryVerdict | undefined => {
  if (row.action !== GeoFenceAction.EXIT) return undefined;
  if (row.isPhantom === true) return "phantom";
  if (row.isPhantom === false) return "real";
  if (row.isInsideRadius === true) return "inferredPhantom";
  if (row.isInsideRadius === false) return "inferredReal";
  return undefined;
};

const isPhantomVerdict = (v?: TrajectoryVerdict): boolean =>
  v === "phantom" || v === "inferredPhantom";

// COYN-1347: heatmap weighting. Phantom (gated or inferred) events get a
// heavier weight than the rest so the overlay surfaces problematic zones.
const heatWeight = (row: GeofenceListItem): number => {
  const v = verdictFor(row);
  return isPhantomVerdict(v) ? 1 : 0.5;
};

// COYN-1347: leaflet.heat has no first-class react-leaflet wrapper, so we
// drive it through useLeafletContext() + an effect. We register the layer
// through the surrounding `layerContainer` (the LayersControl.Overlay's
// ControlledLayer wrapper) when present, so the overlay's checkbox properly
// toggles its visibility on/off. Without that wiring, the heat layer would
// be attached straight to the map and the checkbox would be a no-op.
interface HeatLayerProps {
  points: Array<[number, number, number]>;
}

const HeatLayer: FC<HeatLayerProps> = ({ points }) => {
  const ctx = useLeafletContext();
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return undefined;
    // leaflet.heat ships untyped on @types/leaflet, so we cast through unknown.
    const layer = (
      L as unknown as {
        heatLayer: (
          latlngs: Array<[number, number, number]>,
          options?: Record<string, unknown>,
        ) => L.Layer;
      }
    ).heatLayer(points, {
      radius: 25,
      blur: 18,
      maxZoom: 17,
      minOpacity: 0.35,
    });

    // Prefer the ControlledLayer / LayerGroup container so the overlay
    // checkbox controls us; fall back to the map directly if we're outside
    // a layers control (defensive — keeps the component reusable elsewhere).
    const container = ctx.layerContainer ?? map;
    container.addLayer(layer);
    return () => {
      container.removeLayer(layer);
    };
  }, [ctx, map, points]);

  return null;
};

const GeofenceMap: FC<GeofenceMapProps> = ({
  primary,
  secondary,
  event,
  activeCenter,
  radiusMeters,
  accuracyThresholdMeters,
  participantId,
  currentEventId,
  labels,
  height = 360,
}) => {
  const points = useMemo(
    () => [primary, secondary, event].filter(Boolean) as GeofenceMapPoint[],
    [primary, secondary, event],
  );

  const center = useMemo(
    () => (points.length > 0 ? computeBounds(points) : ([0, 0] as LatLngTuple)),
    [points],
  );

  const externalUrl = useMemo(() => googleMapsUrl(points), [points]);

  // COYN-1347: phantom-EXIT gate buffer. Only renders when we have both a
  // radius and an accuracy threshold and the computed inner radius is > 0.
  const bufferRadius = useMemo(() => {
    if (radiusMeters == null || accuracyThresholdMeters == null) return 0;
    if (radiusMeters <= 0) return 0;
    return computeBufferRadius(radiusMeters, accuracyThresholdMeters);
  }, [radiusMeters, accuracyThresholdMeters]);

  // COYN-1347: fetch participant trajectory. Skipped (via `enabled`) when we
  // don't have a participant id, so callers without that context pay no
  // network cost.
  const trajectoryQuery = useGeofenceSearch(
    {
      pageNumber: 0,
      pageSize: TRAJECTORY_PAGE_SIZE,
      sortField: "timestamp",
      sortOrder: -1,
      participantId: participantId || undefined,
    },
    { enabled: !!participantId },
  );

  // COYN-1347: separate query for the heatmap — same endpoint, larger page
  // size. Two queries instead of one big fetch shared across overlays so
  // toggling the trajectory off keeps the small fetch warm in cache while
  // the heatmap fetch is only paid for when the overlay is added.
  const heatmapQuery = useGeofenceSearch(
    {
      pageNumber: 0,
      pageSize: HEATMAP_PAGE_SIZE,
      sortField: "timestamp",
      sortOrder: -1,
      participantId: participantId || undefined,
    },
    { enabled: !!participantId },
  );

  // COYN-1347: trajectory events with valid coordinates, oldest → newest so
  // the polyline reads left-to-right chronologically.
  const trajectoryEvents = useMemo<GeofenceListItem[]>(() => {
    const all = trajectoryQuery.data?.geofences ?? [];
    return all
      .filter(
        (g) => typeof g.latitude === "number" && typeof g.longitude === "number",
      )
      .slice()
      .reverse();
  }, [trajectoryQuery.data]);

  const trajectoryLine = useMemo<LatLngExpression[]>(
    () => trajectoryEvents.map((g) => [g.latitude, g.longitude]),
    [trajectoryEvents],
  );

  // COYN-1347: heatmap weighted points. We pass (lat, lng, intensity) tuples
  // straight into leaflet.heat.
  const heatPoints = useMemo<Array<[number, number, number]>>(() => {
    const all = heatmapQuery.data?.geofences ?? [];
    return all
      .filter(
        (g) => typeof g.latitude === "number" && typeof g.longitude === "number",
      )
      .map((g) => [g.latitude, g.longitude, heatWeight(g)]);
  }, [heatmapQuery.data]);

  const dateFormat = labels.dateFormat || "DD MMM YYYY, hh:mm A";

  const verdictLabel = (v?: TrajectoryVerdict): string => {
    switch (v) {
      case "phantom":
        return labels.tooltipPhantom || "Phantom";
      case "inferredPhantom":
        return labels.tooltipInferredPhantom || "Phantom (inf.)";
      case "real":
        return labels.tooltipReal || "Real";
      case "inferredReal":
        return labels.tooltipInferredReal || "Real (inf.)";
      default:
        return "";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          height,
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Map
          center={center}
          zoom={16}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <Tiles
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite (Esri)">
              <Tiles
                attribution="Tiles &copy; Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>

            {/* COYN-1347: trajectory overlay (toggleable, off by default
                until the user explicitly opts in via the layers control). */}
            {participantId && trajectoryEvents.length > 0 && (
              <OverlayLayer
                name={labels.trajectory || "Participant trajectory"}
                checked={false}
              >
                <Group>
                  <Line
                    positions={trajectoryLine}
                    pathOptions={{
                      color: TRAJECTORY_LINE_COLOR,
                      weight: 2,
                      opacity: 0.7,
                    }}
                  />
                  {trajectoryEvents.map((g, idx) => {
                    const v = verdictFor(g);
                    const phantom = isPhantomVerdict(v);
                    const isCurrent = g._id === currentEventId;
                    const color = phantom
                      ? TRAJECTORY_PHANTOM_COLOR
                      : TRAJECTORY_REAL_COLOR;
                    const verdictText = verdictLabel(v);
                    const actionText = g.action;
                    const when = formatInBackofficeLondon(
                      g.timestamp,
                      dateFormat,
                    );
                    const tooltipText = `#${idx + 1} ${actionText} • ${when}${verdictText ? ` (${verdictText})` : ""}`;
                    return (
                      <React.Fragment key={`${g._id}-traj`}>
                        {/* Highlight ring around the event we're inspecting */}
                        {isCurrent && (
                          <Dot
                            center={[g.latitude, g.longitude]}
                            radius={10}
                            pathOptions={{
                              color: PRIMARY_COLOR,
                              weight: 3,
                              fillColor: PRIMARY_COLOR,
                              fillOpacity: 0,
                            }}
                          />
                        )}
                        <Dot
                          center={[g.latitude, g.longitude]}
                          radius={5}
                          pathOptions={{
                            color: "white",
                            weight: 1,
                            fillColor: color,
                            fillOpacity: 0.9,
                          }}
                        >
                          <HoverTip>{tooltipText}</HoverTip>
                        </Dot>
                      </React.Fragment>
                    );
                  })}
                </Group>
              </OverlayLayer>
            )}

            {/* COYN-1347: heatmap overlay (toggleable, off by default). */}
            {participantId && heatPoints.length > 0 && (
              <OverlayLayer
                name={labels.heatmap || "Activity heatmap"}
                checked={false}
              >
                <Group>
                  <HeatLayer points={heatPoints} />
                </Group>
              </OverlayLayer>
            )}
          </LayersControl>

          {activeCenter && radiusMeters && radiusMeters > 0 && (
            <RadiusCircle
              center={[activeCenter.lat, activeCenter.lng]}
              radius={radiusMeters}
              pathOptions={{
                color: PRIMARY_COLOR,
                fillColor: PRIMARY_COLOR,
                fillOpacity: 0.08,
                weight: 1,
              }}
            />
          )}

          {/* COYN-1347: phantom-EXIT gate inner threshold. Anchored on the
              same centre the radius circle uses (whichever address matches
              the event's addressType). */}
          {activeCenter && bufferRadius > 0 && (
            <RadiusCircle
              center={[activeCenter.lat, activeCenter.lng]}
              radius={bufferRadius}
              pathOptions={{
                color: EVENT_COLOR,
                weight: 1.5,
                dashArray: "4 4",
                fillOpacity: 0,
              }}
            />
          )}

          {activeCenter && (
            <Line
              positions={[
                [event.lat, event.lng],
                [activeCenter.lat, activeCenter.lng],
              ]}
              pathOptions={{
                color: EVENT_COLOR,
                weight: 2,
                dashArray: "6 6",
                opacity: 0.7,
              }}
            />
          )}

          {primary && (
            <Pin position={[primary.lat, primary.lng]} icon={primaryIcon}>
              <Popup>
                <strong>{labels.primary}</strong>
                <br />
                {primary.label}
                <br />
                {primary.lat.toFixed(6)}, {primary.lng.toFixed(6)}
              </Popup>
            </Pin>
          )}

          {secondary && (
            <Pin
              position={[secondary.lat, secondary.lng]}
              icon={secondaryIcon}
            >
              <Popup>
                <strong>{labels.secondary}</strong>
                <br />
                {secondary.label}
                <br />
                {secondary.lat.toFixed(6)}, {secondary.lng.toFixed(6)}
              </Popup>
            </Pin>
          )}

          <Pin position={[event.lat, event.lng]} icon={eventIcon}>
            <Popup>
              <strong>{labels.event}</strong>
              <br />
              {event.lat.toFixed(6)}, {event.lng.toFixed(6)}
            </Popup>
          </Pin>
        </Map>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}
      >
        {primary && (
          <Chip
            size="small"
            label={labels.primary}
            sx={{
              backgroundColor: PRIMARY_COLOR,
              color: "white",
              fontWeight: 600,
            }}
          />
        )}
        {secondary && (
          <Chip
            size="small"
            label={labels.secondary}
            sx={{
              backgroundColor: SECONDARY_COLOR,
              color: "white",
              fontWeight: 600,
            }}
          />
        )}
        <Chip
          size="small"
          label={labels.event}
          sx={{
            backgroundColor: EVENT_COLOR,
            color: "white",
            fontWeight: 600,
          }}
        />
        {radiusMeters != null && (
          <Typography variant="caption" color="text.secondary">
            {labels.radius}: {radiusMeters} m
          </Typography>
        )}
        {bufferRadius > 0 && labels.bufferCircle && (
          <Typography variant="caption" color="error.main">
            {labels.bufferCircle}: {Math.round(bufferRadius)} m
          </Typography>
        )}
        {participantId && labels.trajectoryHint && (
          <Typography variant="caption" color="text.secondary">
            • {labels.trajectoryHint}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12 }}
        >
          {labels.openInGoogleMaps} ↗
        </a>
      </Stack>
    </Box>
  );
};

export default GeofenceMap;
